import type { ReactNode } from 'react'
import { Icon } from './Icon'
import { EmptyState, ErrorState, LoadingState, cx } from './ui'

export interface Column<T> {
  key: string
  header: string
  cell: (row: T) => ReactNode
  align?: 'left' | 'right' | 'center'
  /** API ordering field; makes the header a sort button. */
  sortKey?: string
  /** Hide on narrow screens when the stacked layout would be too long. */
  priority?: 'low'
}

interface Props<T> {
  caption: string
  columns: Column<T>[]
  rows: T[] | undefined
  rowKey: (row: T) => string | number
  loading?: boolean
  error?: unknown
  onRetry?: () => void
  emptyTitle?: string
  emptyHint?: string
  emptyAction?: ReactNode
  ordering?: string
  onOrdering?: (ordering: string) => void
  onRowClick?: (row: T) => void
  rowLabel?: (row: T) => string
  rowClass?: (row: T) => string | undefined
}

/** Table on wide screens; each row becomes a labelled card on phones (CSS reads the data-label attributes). */
export function DataTable<T>({ caption, columns, rows, rowKey, loading, error, onRetry, emptyTitle = 'Nada que mostrar', emptyHint, emptyAction, ordering, onOrdering, onRowClick, rowLabel, rowClass }: Props<T>) {
  if (error) return <ErrorState error={error} onRetry={onRetry} />
  if (loading || rows === undefined) return <LoadingState label={`Cargando ${caption.toLowerCase()}…`} />
  if (rows.length === 0) return <EmptyState title={emptyTitle} hint={emptyHint} action={emptyAction} />

  const sortField = ordering?.replace(/^-/, '')
  const descending = ordering?.startsWith('-')
  return (
    <div className="table-wrap">
      <table className="data-table">
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            {columns.map((c) => {
              const sorted = c.sortKey && c.sortKey === sortField
              return (
                <th key={c.key} scope="col" className={cx(c.align && `align-${c.align}`, c.priority && 'hide-narrow')} aria-sort={sorted ? (descending ? 'descending' : 'ascending') : undefined}>
                  {c.sortKey && onOrdering ? (
                    <button type="button" className="th-sort" aria-label={`Ordenar por ${c.header}`} onClick={() => onOrdering(sorted && !descending ? `-${c.sortKey}` : c.sortKey!)}>
                      {c.header}
                      <Icon name="chevron-down" size={12} style={{ transform: sorted && !descending ? 'rotate(180deg)' : undefined, opacity: sorted ? 1 : 0.35 }} />
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className={cx(onRowClick && 'is-clickable', rowClass?.(row))} onClick={onRowClick ? () => onRowClick(row) : undefined}>
              {columns.map((c, i) => (
                <td key={c.key} data-label={c.header} className={cx(c.align && `align-${c.align}`, c.priority && 'hide-narrow')}>
                  {c.cell(row)}
                  {onRowClick && i === 0 ? (
                    <button type="button" className="row-hit" aria-label={rowLabel?.(row) ?? 'Abrir fila'} onClick={(e) => { e.stopPropagation(); onRowClick(row) }} />
                  ) : null}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
